import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import responsiveStyles from './responsiveStyles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'

import { titleData } from '../../../data/titles'
import { contactPreferencesData } from '../../../data/contactPreferences'
import {
    InputFieldText,
    InputFieldSelect,
    FieldGroup,
    Field,
    SubmitButton,
    ButtonContainer,
    FieldContainer,
    FormCard,
} from '../../reusable'

import { FormPartInterface, SaveValueInterface } from '../../../interfaces'
import { Checkbox } from '../../reusable/checkbox'
import { InputFieldAddressLookup } from '../../reusable/inputFieldAddressLookup'
import { lenderData } from '../../../data/lenders'
import { SignaturePad } from '../../reusable/sigPad'
import { browserName, osName, deviceDetect, getUA } from 'react-device-detect'
import { useClaimData } from '../../../claimProvider'
import Previewer from '../../reusable/previewer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUpdateClaimData } from '../../../claimProvider'
import { consentWordingV3, CURRENT_CONSENT_WORDING_VERSION } from '../../../data/consentWording'

export const SignatureSentinel = (props: FormPartInterface) => {
    const { saveValueFunct, isActive, assignGroup, validateSection } = props
    const [currentStep, setCurrentStep] = useState(0)
    const [postCurrentStep, setPostCurrentStep] = useState(0)
    const [stepData, setStepData] = useState<any>({})
    // const [cases, setCases] = useState<any>([{ leadData: { lender: "audi finance", case_ref: "pcpSNT_234" }, cfaUrl: "T", loaUrl: "YT" }]);
    // const [cases, setCases] = useState<any>([])
    const [sigLinkToken, setSigLinkToken] = useState<string>('')
    const claim = useClaimData()
    const updateClaim = useUpdateClaimData()
    const navigate = useNavigate()

    const [searchParams] = useSearchParams()
    const chase_id = searchParams.get('chase_id')
    const agent = searchParams.get('agent')

    useEffect(() => {
        console.log('Current claim data:', claim)
    }, [claim])

    const preSaveValueFunct = (dataToSave: SaveValueInterface) => {
        const saved = saveValueFunct(dataToSave)
        let tempStepData = { ...stepData }
        tempStepData[dataToSave.field] = dataToSave.value
        setStepData(tempStepData)
        console.log('Updated stepData:', tempStepData)
        return saved
    }

    const animateBeforeAssign = (assignment: string) => {
        console.log('Step data:', stepData)

        // Consent checkboxes are already gated by SignaturePad's requiredFields
        // (driven by consentWordingV3), so no separate sectionValidation check is needed here.
        setTimeout(() => {
            assignGroup('complete')
            navigate('/thankyou')
        }, 400)
    }

    const toBool = (value: unknown) => value === 'yes' || value === true

    const buildSignClaim = () => {
        const consentValues = consentWordingV3.reduce<Record<string, boolean>>((acc, item) => {
            acc[item.fieldName] = toBool(claim?.[item.fieldName])
            return acc
        }, {})
        return {
            ...claim,
            ...consentValues,
            consentWordingVersion: CURRENT_CONSENT_WORDING_VERSION,
        }
    }

    const getCurrentStep = () => {
        return isActive == 'signature' ? (
            <FieldGroup title="Sign Your Letter of Authority">
                <FormCard assignPostStep={setPostCurrentStep} step={currentStep} onBack={() => assignGroup('confirm')}>
                    <div className={`${styles.sentinelSig} ${responsiveStyles.sentinelSig}`}>
                        <div className={`${styles.signatureSection} ${responsiveStyles.signatureSection}`}>
                            <h3 className={`${styles.signatureTitle} ${responsiveStyles.signatureTitle}`}>
                                Provide Your Signature
                            </h3>
                            <p className={`${styles.signatureInstructions} ${responsiveStyles.signatureInstructions}`}>
                                In order to be able to identify and submit your claim, we need your signature. By
                                signing below, you are giving Sentinel Legal authority to act on your behalf and to
                                apply your signature to the documents above, which you agree to.
                            </p>

                            <SignaturePad
                                extraPayload={{
                                    browser: browserName,
                                    device: osName,
                                    type: claim.leadOrigin,
                                    chase_id,
                                    agent,
                                    claim: buildSignClaim(),
                                }}
                                additionalAction={() => animateBeforeAssign('complete')}
                                stepData={stepData}
                                requiredFields={consentWordingV3.map((item) => item.fieldName)}
                            >
                                <section
                                    className={`${styles.consentDisclosure} ${responsiveStyles.consentDisclosure}`}
                                >
                                    <h3>ViewThru consent</h3>
                                    <p>
                                        I understand that a number of entities working together are involved in gathering
                                        and considering information to assess my anticipated claim. I therefore understand
                                        and agree that:
                                    </p>
                                    <ul>
                                        <li>
                                            ViewThru will carry out a ‘soft’ credit search about me with Credit Reference
                                            Agency Equifax Limited, and the results will be shared with Sentinel Legal for
                                            purposes of considering my anticipated claim. I’m aware that this will not
                                            impact my credit score.
                                        </li>
                                        <li>
                                            For the purpose of proceeding with the anticipated claim, I acknowledge that I
                                            am required to be based within the United Kingdom. Therefore, as part of
                                            assessing my eligibility for the anticipated claim, my IP address and/or mobile
                                            phone location data may be used to confirm that I am in the United Kingdom.
                                            These results will also be shared with Sentinel Legal.
                                        </li>
                                        <li>
                                            I understand that I may withdraw my consent at any time in respect of the above
                                            by contacting ViewThru, and this will not affect processing carried out before
                                            I withdrew consent.
                                        </li>
                                    </ul>
                                    <p>
                                        I acknowledge that I have read and agreed to{' '}
                                        <a href="https://www.viewthru.co.uk/privacy" target="_blank" rel="noreferrer">
                                            ViewThru’s Privacy Policy
                                        </a>
                                        .{' '}
                                        <a
                                            href="https://www.sentinellegal.co.uk/privacy-policy"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Sentinel Legal’s Privacy Policy
                                        </a>{' '}
                                        can also be accessed here.
                                    </p>
                                    <p>
                                        By clicking the button below, I give my acknowledgement and consent to the above
                                        provisions freely, expressly, and on an informed basis.
                                    </p>
                                </section>
                                {consentWordingV3.map((item) => (
                                    <div key={item.fieldName} className={`${styles.consentBox} ${responsiveStyles.consentBox}`}>
                                        <Checkbox fieldName={item.fieldName} label={item.label} saveValue={preSaveValueFunct} />
                                    </div>
                                ))}
                            </SignaturePad>
                        </div>
                    </div>
                </FormCard>
            </FieldGroup>
        ) : (
            <></>
        )
    }

    return getCurrentStep()
}
